import React from 'react'
import { observer } from 'mobx-react'
import Link from '../../models/Link'
import { computed, makeObservable, observable } from 'mobx';
import ApiClient from '../../api/ApiClient'
import { Button, ButtonGroup, Card, CardContent, CircularProgress, FormGroup, Grid, Input, InputAdornment, TextField } from '@mui/material'
import { NavigationListener } from './DStepper'
import { AcceptJsAuthData, BankAccountDataType, CreditCardDataType, loadAcceptJs } from '../../common/AuthNet'
import DialogManager from '../dialog-provider/DialogManager'
import ApiClientFactory from '../../api/ApiClientFactory'
// @ts-ignore
import * as cardValidator from 'card-validator'
import _ from 'lodash'
import AccountBox from '@mui/icons-material/AccountBox'
import AccountBalance from '@mui/icons-material/AccountBalance'
import CreditCard from '@mui/icons-material/CreditCard'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Lock from '@mui/icons-material/Lock'
import LocationOn from '@mui/icons-material/LocationOn'
import DynamicIcon from '../DynamicIcon'
import moment from 'moment'
import { Autocomplete } from '@mui/lab'
import { v4 as uuidv4 } from 'uuid'
import { CyberSourceClient, CyberSourceKey } from '../../common/CyberSource'
import { HomeOutlined } from '@mui/icons-material'

type Props = {
  url: string
  navigationListener: NavigationListener
  onChange: (id: number) => void
}

export type TokenizedData = {
  dataDescriptor: string
  dataValue: string
}

type AuthNetTokenizer = {
  type: 'Accept.js'
  authentication: {
    clientKey: string
    name: string
  }
}

type CyberSourceTokenizer = {
  type: 'CyberSource'
  authentication: {
    keyId: string
    der: {
      format: string
      algorithm: string
      publicKey: string
    }
    jwk: {
      kty: string
      use: string
      kid: string
      n: string
      e: string
    }
  }
}

type CyberSourceMicroformTokenizer = {
  type: 'CyberSourceMicroform'
  authentication: {
    captureContext: string  // the JWT from your backend
  }
}

type OtherTokenizer = {
  type: 'Other'
}

type PaymentMethodTokenizer = AuthNetTokenizer | CyberSourceTokenizer | CyberSourceMicroformTokenizer | OtherTokenizer

type NewPaymentMethodType = {
  uuid?: string
  name: string
  formType: string
  method: string
  tokenizer: PaymentMethodTokenizer
}

type SavedPaymentMethodType = {
  id: number
  displayDetails: {
    icon: string
    label: string
    subLabel: string
    removeLabel: string
    removeConfirmation: string
    removeRel: string
  }
}

type PaymentMethodData = {
  paymentMethodHeader: string
  paymentFormLocale: string
  savedPaymentMethods: SavedPaymentMethodType[]
  preferredPaymentMethod: number | null
  newPaymentMethodHeader: string
  newPaymentMethods: NewPaymentMethodType[]
  postback: {
    label: string
    variant: 'contained' | 'outlined'
    icon: string
    postbackUrl: string
    postbackData: {
      save: boolean,
      type: string
      description: string
      last4: string
      method: string
      token: any
    }
    route: string
  }
  links: Link[]
}

const DPaymentMethod = observer(class DPaymentMethod extends React.Component<Props> {
  private paymentMethodData?: PaymentMethodData;
  private error?: string;
  private loading = false;
  private preferredPaymentMethodRemoved = false;

  private cardData = {
    nameOnCard: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    securityCode: '',
    zipCode: '',
  };

  private bankData = {
    nameOnAccount: '',
    routingNumber: '',
    accountNumber: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
  };

  private microformInitialized = false;
  private microformInstance: any = null;  // will hold the microform object
  private flexScriptLoaded = false;

  selectedNewPaymentMethodUuid?: string;

  constructor (props: Props) {
    super(props);

    makeObservable<DPaymentMethod, "paymentMethodData" | "error" | "loading" | "preferredPaymentMethodRemoved" | "cardData" | "bankData" | "microformInitialized" | "microformInstance" | "flexScriptLoaded">(this, {
      paymentMethodData: observable,
      error: observable,
      loading: observable,
      preferredPaymentMethodRemoved: observable,
      cardData: observable,
      bankData: observable,
      selectedNewPaymentMethodUuid: observable,
      selectedNewPaymentMethod: computed,
      paymentMethodFormType: computed,
      preferredPaymentMethod: computed,
      microformInitialized: observable,
      microformInstance: observable,
      flexScriptLoaded: observable,
    });
  }

  get selectedNewPaymentMethod (): NewPaymentMethodType | undefined {
    return this.selectedNewPaymentMethodUuid
      ? this.paymentMethodData?.newPaymentMethods.find(pm => pm.uuid === this.selectedNewPaymentMethodUuid)
      : undefined
  }

  get paymentMethodFormType () {
    if (this.selectedNewPaymentMethod) {
      if (this.selectedNewPaymentMethod.method === 'credit' || this.selectedNewPaymentMethod.method === 'debit' || this.selectedNewPaymentMethod.method === 'hsafsa') {
        return 'card'
      } else if (this.selectedNewPaymentMethod.method === 'ach') {
        return 'bank'
      }
    }

    return undefined
  }

  get preferredPaymentMethod (): SavedPaymentMethodType | undefined {
    const paymentMethodData = this.paymentMethodData
    if (!this.preferredPaymentMethodRemoved && paymentMethodData && paymentMethodData.preferredPaymentMethod) {
      return _.find(paymentMethodData.savedPaymentMethods, pm => String(pm.id) === String(paymentMethodData.preferredPaymentMethod))
    }

    return undefined
  }

  componentDidMount (): void {
    if (!this.paymentMethodData) {
      this.loadPaymentMethodData()
    }

    this.props.navigationListener.addListener(this.onStepNavigation)
  }

  componentWillUnmount (): void {
    this.props.navigationListener.removeListener(this.onStepNavigation)
  }

  private onStepNavigation = (action: string, next: Function) => {
    if (action === 'Next') {
      if (this.preferredPaymentMethod) {
        this.props.onChange(this.preferredPaymentMethod.id)
        next()
      } else {
        // tokenize payment method
        loadAcceptJs()
          .then(acceptJs => {
            let paymentData: {type: 'card', cardData: CreditCardDataType} | {type: 'bank', bankData: BankAccountDataType} | undefined = undefined

            let last4 = ''
            let description = ''
            let cardType: string | undefined

            if (this.paymentMethodFormType === 'card') {
              if (this.cardData.zipCode.trim().length < 1) {
                DialogManager.show({
                  title: 'Error',
                  content: <div>
                    Billing zip code is required
                  </div>,
                })
                return
              }

              paymentData = {
                type: 'card',
                cardData: {
                  cardNumber: this.cardData.cardNumber,
                  month: this.cardData.expMonth,
                  year: this.cardData.expYear,
                  zip: this.cardData.zipCode,
                  cardCode: this.cardData.securityCode,
                  fullName: this.cardData.nameOnCard,
                },
              }

              const numberValidation = cardValidator.number(this.cardData.cardNumber)

              if (numberValidation.card) {
                switch (numberValidation.card.type) {
                  case 'visa':
                    description = 'Visa'
                    cardType = '001'
                    break;
                  case 'mastercard':
                    description = 'MasterCard'
                    cardType = '002'
                    break;
                  case 'american-express':
                    description = 'American Express'
                    cardType = '003'
                    break;
                  case 'discover':
                    description = 'Discover'
                    cardType = '004'
                    break;
                  default:
                    description = 'Credit/Debit Card'
                    break;
                }
              } else {
                description = 'Credit/Debit Card'
              }

              last4 = this.cardData.cardNumber.substr(this.cardData.cardNumber.length - 4)
            } else if (this.paymentMethodFormType === 'bank') {
              paymentData = {
                type: 'bank',
                bankData: {
                  accountType: 'checking',
                  nameOnAccount: this.bankData.nameOnAccount,
                  routingNumber: this.bankData.routingNumber,
                  accountNumber: this.bankData.accountNumber,
                },
              }

              description = 'Checking'
              last4 = this.bankData.accountNumber.substr(this.bankData.accountNumber.length - 4)
            }

            if (paymentData) {
              if (this.selectedNewPaymentMethod?.tokenizer.type === 'Accept.js') {
                const authData: AcceptJsAuthData = {
                  apiLoginID: this.selectedNewPaymentMethod.tokenizer.authentication.name,
                  clientKey: this.selectedNewPaymentMethod.tokenizer.authentication.clientKey,
                }

                let extraData = {}

                if (this.paymentMethodFormType === 'bank') {
                  extraData = {
                    ...extraData,
                    address: this.bankData.address,
                    city: this.bankData.city,
                    state: this.bankData.state,
                    zipCode: this.bankData.zipCode,
                    nameOnCard: this.cardData.nameOnCard,
                  }
                }

                return acceptJs.tokenizePaymentMethod(authData, paymentData)
                  .then((response: AcceptJsResponseDataType) => {
                    ApiClientFactory.getInstance()
                      .post(this.paymentMethodData!.postback.postbackUrl, {
                        type: this.paymentMethodFormType,
                        last4: last4,
                        description: description,
                        token: response.opaqueData,
                        ...extraData,
                      })
                      .then(response => {
                        this.props.onChange(response.data.preferredPaymentMethod)
                        next()
                      })
                      .catch(err => {
                        let extendedError: string | undefined = undefined
                        if (err.isAxiosError && err.response.status === 400 && _.isString(err.response.data)) {
                          extendedError = err.response.data
                        }

                        DialogManager.show({
                          title: 'Error',
                          content: `There was an error handling your payment method.${extendedError ? ` ${extendedError}` : ''}`,
                        })
                      })
                  })
                  .catch((response: AcceptJsResponseDataType) => {
                    const errors = response.messages.message.map(message => message.text)

                    DialogManager.show({
                      title: 'Error',
                      content: <div>
                        There was an error with the payment processor
                        {errors.map((e, idx) => <div key={idx}>{e}</div>)}
                      </div>,
                    })
                  })
              } else if (this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSource') {
                const authData: CyberSourceKey = {
                  kid: this.selectedNewPaymentMethod.tokenizer.authentication.jwk.kid,
                  keystore: this.selectedNewPaymentMethod.tokenizer.authentication.jwk,
                }

                const csTokenizer = paymentData.type === 'card'
                  ? (new CyberSourceClient()).tokenizePaymentMethod(authData, {
                    cardType: cardType ?? '',
                    cardNumber: paymentData.cardData.cardNumber,
                    securityCode: paymentData.cardData.cardCode || '',
                    expiryMonth: paymentData.cardData.month,
                    expiryYear: paymentData.cardData.year,
                  }).then(response => ({
                    type: 'card',
                    data: response,
                  }))
                  : new Promise((resolve, reject) => {
                    const bankData = (paymentData as {bankData: BankAccountDataType}).bankData
                    const data = {
                      accountType: bankData.accountType,
                      accountNumber: bankData.accountNumber,
                      nameOnAccount: bankData.nameOnAccount,
                      routingNumber: bankData.routingNumber,
                    }
                    resolve({
                      type: 'bank',
                      data,
                    })
                  })

                return csTokenizer.then((response: any) => {
                  let extraData = {}

                  if (paymentData?.type === 'card') {
                    extraData = {
                      ...extraData,
                      expMonth: paymentData.cardData.month,
                      expYear: paymentData.cardData.year,
                      zipCode: this.cardData.zipCode,
                      nameOnCard: this.cardData.nameOnCard,
                    }
                  } else if (paymentData?.type === 'bank') {
                    extraData = {
                      ...extraData,
                      address: this.bankData.address,
                      city: this.bankData.city,
                      state: this.bankData.state,
                      zipCode: this.bankData.zipCode,
                    }
                  }

                  ApiClientFactory.getInstance()
                    .post(this.paymentMethodData!.postback.postbackUrl, {
                      type: this.paymentMethodFormType,
                      last4: last4,
                      description: description,
                      token: response,
                      ...extraData,
                    })
                    .then(response => {
                      this.props.onChange(response.data.preferredPaymentMethod)
                      next()
                    })
                    .catch(err => {
                      let extendedError: string | undefined = undefined
                      if (err.isAxiosError && err.response.status === 400 && _.isString(err.response.data)) {
                        extendedError = err.response.data
                      }

                      DialogManager.show({
                        title: 'Error',
                        content: `There was an error handling your payment method.${extendedError ? ` ${extendedError}` : ''}`,
                      })
                    })
                })
                  .catch((err: any) => {
                    DialogManager.show({
                      title: 'Error',
                      content: <div>
                        There was an error with the payment processor
                      </div>,
                    })

                  })
              } else if (this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSourceMicroform') {
                if(!this.microformInitialized || !this.microformInstance) {
                  DialogManager.show({ title: 'Error', content: 'Payment processor not ready' })
                  return
                }

                const options = {
                  expirationMonth: this.cardData.expMonth.padStart(2, '0'),
                  expirationYear: this.cardData.expYear,
                }

                this.microformInstance.createToken(options, (err: any, transientToken: string) => {
                  if (err) {
                    DialogManager.show({
                      title: 'Error',
                      content: <div>{err.message || 'There was an error with the payment processor'}</div>
                    })
                    return
                  }

                  // Decode transient token to get card details
                  try {
                    const payload = JSON.parse(atob(transientToken.split('.')[1]))
                    const cardInfo = payload.content.paymentInformation.card.number
                    last4 = cardInfo.maskedValue.slice(-4)
                    const detectedTypes = cardInfo.detectedCardTypes || []
                    const typeCode = detectedTypes[0] // e.g. "003"

                    switch (typeCode) {
                      case '001': description = 'Visa'; break
                      case '002': description = 'MasterCard'; break
                      case '003': description = 'American Express'; break
                      case '004': description = 'Discover'; break
                      default: description = 'Credit/Debit Card'
                    }
                  } catch (e) {
                    last4 = '****'
                    description = 'Credit/Debit Card'
                  }

                  ApiClientFactory.getInstance().post(this.paymentMethodData!.postback.postbackUrl, {
                    type: this.paymentMethodFormType,
                    last4: last4,
                    description: description,
                    token: transientToken,  // this is the new token format
                    expMonth: this.cardData.expMonth,
                    expYear: this.cardData.expYear,
                    zipCode: this.cardData.zipCode,
                    nameOnCard: this.cardData.nameOnCard,
                  }).then(response => {
                    this.props.onChange(response.data.preferredPaymentMethod);
                    next();
                  }).catch(err => {
                    // same error handling as before
                  });
                });
              } else {
                // invalid tokenizer
              }
            } else {
              // invalid payment type
            }
          })
          .catch(() => DialogManager.show({
            title: 'Error',
            content: 'There was an error loading the payment processor interface',
          }))
      }

      return true
    } else {
      return false
    }
  }

  componentDidUpdate() {
    if (
      this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSourceMicroform' &&
      this.paymentMethodFormType === 'card' &&
      !this.microformInitialized &&
      document.getElementById('microform-number-container')
    ) {
      const tokenizer = this.selectedNewPaymentMethod.tokenizer;

      const captureContext = typeof tokenizer.authentication === 'string'
        ? tokenizer.authentication
        : tokenizer.authentication?.captureContext;

      if (!captureContext || typeof captureContext !== 'string') {
        console.error('No valid capture context found:', captureContext);
        DialogManager.show({
          title: 'Error',
          content: 'Payment configuration missing. Please contact support.'
        });
        return;
      }

      this.initializeMicroform(captureContext);
    }
  }

  private initializeMicroform = (captureContext: string) => {
    if (this.microformInitialized || !captureContext) return;

    // Validate it's a JWT (has 3 parts separated by dots)
    const parts = captureContext.split('.');
    if (parts.length !== 3) {
      console.error('Invalid JWT format - expected 3 parts');
      DialogManager.show({
        title: 'Error',
        content: 'Invalid payment token format.'
      });
      return;
    }

    try {
      const payload = JSON.parse(atob(parts[1]));

      const clientLibrary = payload.ctx[0].data.clientLibrary;
      const clientLibraryIntegrity = payload.ctx[0].data.clientLibraryIntegrity || null;

      if (!clientLibrary) {
        throw new Error('clientLibrary URL not found in JWT payload');
      }

      console.log('Loading Flex SDK from:', clientLibrary);

      // Remove any existing script to prevent duplicates
      const existing = document.querySelector('script[src*="flex.cybersource.com"]');
      if (existing) existing.remove();

      const script = document.createElement('script');
      script.src = clientLibrary;
      script.async = true;
      if (clientLibraryIntegrity) {
        script.integrity = clientLibraryIntegrity;
        script.crossOrigin = 'anonymous';
      }

      script.onload = () => {
        console.log('Flex SDK script loaded successfully');
        this.setupMicroform(captureContext);
      };

      script.onerror = () => {
        console.error('Failed to load Flex SDK script');
        DialogManager.show({
          title: 'Error',
          content: 'Failed to load payment processor. Please refresh.'
        });
      };

      document.head.appendChild(script);

    } catch (err) {
      console.error('Failed to parse capture context:', err);
      DialogManager.show({
        title: 'Error',
        content: 'Invalid payment configuration.'
      });
    }
  };

  private setupMicroform = (captureContext: string) => {
    if (!(window as any).Flex || this.microformInitialized) {
      console.error('Flex not available on window');
      return;
    }

    console.log('Initializing Microform');

    const myStyles = {
      input: {
        'font-size': '16px',
        'font-family': 'Roboto, Helvetica, Arial, sans-serif',
        'font-weight': '500',
        'color': 'rgba(0,0,0,0.87)',
        'padding': '6px 0 7px 0',
        'line-height': '1.4375em',
        //'box-sizing': 'border-box',
        'border': 'none',
        'border-bottom': '1px solid rgba(0,0,0,0.42)'
      },
      '::placeholder': {
        color: '#999',
        opacity: 1
      },
      ':focus': {
        color: 'rgba(0,0,0,0.87)',
        'outline': 'none',
        'border-bottom-color': 'rgba(0,0,0,0.87)'
      },
      'valid': { color: 'rgba(0,0,0,0.87)' },
      'invalid': { color: '#d32f2f' }
    };

    try {
      const flex = new (window as any).Flex(captureContext);
      const microform = flex.microform({ styles: myStyles });

      const number = microform.createField('number', { placeholder: 'Card Number' });
      const securityCode = microform.createField('securityCode', { placeholder: 'CVV' });

      const numberContainer = document.getElementById('microform-number-container');
      const cvvContainer = document.getElementById('microform-securityCode-container');

      if (!numberContainer || !cvvContainer) {
        console.error('Containers not found in DOM');
        return;
      }

      number.load('#microform-number-container');
      securityCode.load('#microform-securityCode-container');

      this.microformInstance = microform;
      this.microformInitialized = true;

      console.log('Microform fields loaded successfully');
    } catch (err) {
      console.error('Microform setup failed:', err);
      DialogManager.show({
        title: 'Error',
        content: 'Failed to initialize secure fields.'
      });
    }
  };

  private loadPaymentMethodData = () => {
    this.loading = true
    this.preferredPaymentMethodRemoved = false

    ApiClient.getInstance().get(this.props.url)
      .then(response => {
        this.paymentMethodData = response.data
        this.paymentMethodData?.newPaymentMethods.forEach(pm => pm.uuid = uuidv4().toString())
        this.selectedNewPaymentMethodUuid = this.paymentMethodData!.newPaymentMethods.length ? this.paymentMethodData!.newPaymentMethods[0].uuid : undefined
      })
      .catch(error => this.error = 'There was an error loading the payment method data')
      .then(() => this.loading = false)
  }

  private renderPaymentForm = () => {
    const paymentMethodData = this.paymentMethodData
    if (this.preferredPaymentMethod) {
      return this.renderSavedPaymentMethod(this.preferredPaymentMethod)
    } else if (paymentMethodData && paymentMethodData.newPaymentMethods.length) {
      return <div>
        <ButtonGroup color="primary" aria-label="button group">
          {
            paymentMethodData.newPaymentMethods.map(pm => <Button
              key={pm.method}
              size="small"
              variant="contained"
              color={pm.uuid === this.selectedNewPaymentMethod?.uuid ? 'primary' : 'inherit'}
              onClick={() => this.selectedNewPaymentMethodUuid = pm.uuid}
            >{pm.name}</Button>)
          }
        </ButtonGroup>
        <div>
          {
            this.selectedNewPaymentMethod
              ? this.renderPaymentMethodForm(this.selectedNewPaymentMethod.method)
              : null
          }
        </div>
      </div>
    } else {
      return 'No payment methods'
    }
  }

  private renderSavedPaymentMethod = (paymentMethod: SavedPaymentMethodType) => {
    return <Card>
      <CardContent style={{ paddingBottom: 16 }}>
        <div style={{ display: 'flex' }}>
          <div style={{ flex: 1 }}>
            <div><b>{paymentMethod.displayDetails.label}</b></div>
            {
              paymentMethod.displayDetails.subLabel
                ? <div>{paymentMethod.displayDetails.subLabel}</div>
                : null
            }
          </div>
          <div style={{ paddingLeft: 6 }}>
            <DynamicIcon icon={paymentMethod.displayDetails.icon} style={{ width: 60 }}/>
          </div>
        </div>
        <Button
          color="primary"
          onClick={() => this.preferredPaymentMethodRemoved = true}
        >{paymentMethod.displayDetails.removeLabel}</Button>
      </CardContent>
    </Card>
  }

  private renderPaymentMethodForm = (type: string) => {
    if (this.paymentMethodFormType === 'card') {
      const isMicroform = this.selectedNewPaymentMethod?.tokenizer.type === 'CyberSourceMicroform';

      return (
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          {/* Name on Card */}
          <Grid item xs={12}>
            <FormGroup row>
              <Input
                id="nameoncard"
                name="nameoncard"
                fullWidth
                value={this.cardData.nameOnCard}
                onChange={ev => this.cardData.nameOnCard = ev.target.value}
                placeholder="Name on Card"
                startAdornment={
                  <InputAdornment position="start">
                    <AccountBox style={{ color: '#999' }} />
                  </InputAdornment>
                }
                inputProps={{
                  autoComplete: 'cc-name',
                }}
              />
            </FormGroup>
          </Grid>

          {/* Card Number */}
          <Grid item xs={12}>
            <FormGroup row>
              {isMicroform ? (
                <div style={{ width: '100%', position: 'relative' }}>
                  <div className="microform-field" id="microform-number-container" />
                  <CreditCard style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none', zIndex: 10, fontSize: 24 }} />
                </div>
              ) : (
                <Input
                  id="cardnumber"
                  name="cardnumber"
                  fullWidth
                  value={this.cardData.cardNumber}
                  onChange={ev => this.cardData.cardNumber = ev.target.value}
                  placeholder="Card Number"
                  startAdornment={
                    <InputAdornment position="start">
                      <CreditCard style={{ color: '#999' }} />
                    </InputAdornment>
                  }
                  inputProps={{ autoComplete: 'cc-number' }}
                />
              )}
            </FormGroup>
          </Grid>

          {/* Expiry Month */}
          <Grid item xs={6}>
            <FormGroup row>
              <Autocomplete
                options={['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']}
                id="cc-exp-month"
                style={{ width: '100%' }}
                inputValue={this.cardData.expMonth}
                onInputChange={(event, newInputValue) => this.cardData.expMonth = newInputValue}
                renderInput={(params) => {
                  const props = {
                    ...params,
                    InputProps: {
                      ...(params.InputProps),
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday style={{ color: '#999' }} />
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...(params.inputProps),
                        autoComplete: 'cc-exp-month',
                      },
                    },
                  };
                  return (
                    <TextField
                      variant="standard"
                      fullWidth
                      type="number"
                      {...props}
                      placeholder="Exp. MM"
                      margin="none"
                    />
                  );
                }}
              />
            </FormGroup>
          </Grid>

          {/* Expiry Year */}
          <Grid item xs={6}>
            <FormGroup row>
              <Autocomplete
                options={_.range(moment().year(), moment().year() + 11).map(y => String(y))}
                id="cc-exp-year"
                style={{ width: '100%' }}
                inputValue={this.cardData.expYear}
                onInputChange={(event, newInputValue) => this.cardData.expYear = newInputValue}
                renderInput={(params) => {
                  const props = {
                    ...params,
                    InputProps: {
                      ...(params.InputProps),
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday style={{ color: '#999' }} />
                        </InputAdornment>
                      ),
                      inputProps: {
                        ...(params.inputProps),
                        autoComplete: 'cc-exp-year',
                      },
                    },
                  };
                  return (
                    <TextField
                      variant="standard"
                      fullWidth
                      type="number"
                      {...props}
                      placeholder="YYYY"
                      margin="none"
                    />
                  );
                }}
              />
            </FormGroup>
          </Grid>

          {/* CVV */}
          <Grid item xs={6}>
            <FormGroup row>
              {isMicroform ? (
                <div style={{ width: '100%', position: 'relative' }}>
                  <div className="microform-field" id="microform-securityCode-container" />
                  <Lock style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none', zIndex: 10, fontSize: 24 }} />
                </div>
              ) : (
                <Input
                  id="cvv2"
                  name="cvv2"
                  fullWidth
                  value={this.cardData.securityCode}
                  onChange={ev => this.cardData.securityCode = ev.target.value}
                  placeholder="CVV"
                  startAdornment={
                    <InputAdornment position="start">
                      <Lock style={{ color: '#999' }} />
                    </InputAdornment>
                  }
                  inputProps={{ autoComplete: 'cc-csc' }}
                />
              )}
            </FormGroup>
          </Grid>

          {/* Zip Code */}
          <Grid item xs={6}>
            <FormGroup row>
              <Input
                id="postal-code"
                name="postal-code"
                inputProps={{
                  autoComplete: 'shipping postal-code',
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <LocationOn style={{ color: '#999' }} />
                  </InputAdornment>
                }
                placeholder="Zip Code"
                fullWidth
                value={this.cardData.zipCode}
                onChange={ev => this.cardData.zipCode = ev.target.value}
              />
            </FormGroup>
          </Grid>
        </Grid>
      );
    } else if (this.paymentMethodFormType === 'bank') {
      return (
        <Grid container spacing={2} style={{ marginTop: 10 }}>
          <Grid item xs={12}>
            <FormGroup row>
              <Input
                placeholder="Name on Bank Account"
                startAdornment={
                  <InputAdornment position="start">
                    <AccountBox style={{ color: '#999' }} />
                  </InputAdornment>
                }
                fullWidth
                value={this.bankData.nameOnAccount}
                onChange={ev => this.bankData.nameOnAccount = ev.target.value}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <Input
                placeholder="Routing Number"
                startAdornment={
                  <InputAdornment position="start">
                    <AccountBalance style={{ color: '#999' }} />
                  </InputAdornment>
                }
                fullWidth
                value={this.bankData.routingNumber}
                onChange={ev => this.bankData.routingNumber = ev.target.value}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <Input
                placeholder="Account Number"
                startAdornment={
                  <InputAdornment position="start">
                    <AccountBox style={{ color: '#999' }} />
                  </InputAdornment>
                }
                fullWidth
                value={this.bankData.accountNumber}
                onChange={ev => this.bankData.accountNumber = ev.target.value}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <Input
                placeholder="Address"
                startAdornment={
                  <InputAdornment position="start">
                    <HomeOutlined style={{ color: '#999' }} />
                  </InputAdornment>
                }
                fullWidth
                value={this.bankData.address}
                onChange={ev => this.bankData.address = ev.target.value}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={6}>
            <FormGroup row>
              <Input
                placeholder="City"
                fullWidth
                value={this.bankData.city}
                onChange={ev => this.bankData.city = ev.target.value}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={2}>
            <FormGroup row>
              <Input
                placeholder="State"
                fullWidth
                value={this.bankData.state}
                onChange={ev => this.bankData.state = ev.target.value}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={4}>
            <FormGroup row>
              <Input
                placeholder="Zip"
                fullWidth
                value={this.bankData.zipCode}
                onChange={ev => this.bankData.zipCode = ev.target.value}
              />
            </FormGroup>
          </Grid>
        </Grid>
      );
    } else {
      return null;
    }
  };
  

  render () {
    return this.loading
      ? <CircularProgress/>
      : this.error
        ? <div>{this.error}</div>
        : this.renderPaymentForm()
  }
});

export default DPaymentMethod
